import { graphql } from 'client/gql';
import { BpFile } from 'components/Files/hooks/useFiles';
import { Submission } from 'components/Submissions/hooks/useSubmissions';
import dayjs from 'dayjs';
import { useMatrixClient } from 'hooks/matrix/useMatrixClient';
import { useAutoUpdateQuery } from 'hooks/useAutoUpdateQuery';
import { useConfirm } from 'hooks/useConfirm';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'urql';
import { naturalStringSort } from 'utils/stringHelper';
import { showSuccessDeleteToast, showUserDbErrorToast } from 'utils/toasts';

export const CreateAssignmentMutation = graphql(`
  mutation BpCreateAssignments($input: [AssignmentCreateInput!]!) {
    createAssignments(input: $input) {
      assignments {
        __typename
        uuid
        title
      }
    }
  }
`);

export const UpdateAssignmentMutation = graphql(`
  mutation BpUpdateAssignment($where: AssignmentWhere!, $update: AssignmentUpdateInput!) {
    updateAssignments(where: $where, update: $update) {
      assignments {
        __typename
        uuid
        title
      }
    }
  }
`);

export const DeleteAssignmentMutation = graphql(`
  mutation BpDeleteAssignments($where: AssignmentWhere!) {
    deleteAssignments(where: $where) {
      nodesDeleted
      relationshipsDeleted
    }
  }
`);

const UseAssignmentsQuery = graphql(`
  query UseAssignmentsData($courseUuid: ID, $assignmentUuid: ID, $teachingUnitUuid: ID) {
    assignments(
      where: {
        uuid: $assignmentUuid
        holder: { TeachingUnit: { active: true, uuid: $teachingUnitUuid, group: { uuid: $courseUuid } } }
      }
    ) {
      __typename
      uuid
      title
      created
      allowSubmissionAfterDueDate
      visibleFrom {
        __typename
        uuid
        date
      }
      dueDate {
        __typename
        uuid
        date
      }
      course {
        __typename
        uuid
        name
        organization {
          __typename
          uuid
        }
        subject {
          __typename
          uuid
          name
          shortName
        }
      }
      holder {
        ... on TeachingUnit {
          __typename
          uuid
          title
        }
      }
      material {
        __typename
        uuid
        text
        fileEntries {
          __typename
          uuid
          filename
          mediaType
          size
          publicDownloadToken
          downloadToken
        }
      }
      submissions {
        __typename
        uuid
        status
        public
        owner {
          __typename
          uuid
          displayName
        }
      }
    }
  }
`);

export type Assignment = {
  uuid: string;
  title: string;
  visibleFrom: Date;
  dueDate: Date;
  createdAt: Date;
  allowSubmissionAfterDueDate: boolean;
  course: {
    uuid: string;
    title: string;
    organizationUuid: string;
    subject: {
      uuid: string;
      name: string;
      shortName: string;
    } | null;
  };
  teachingUnit: {
    uuid: string;
    title: string;
  };
  material: {
    uuid: string;
    text: string | null;
    files: BpFile[];
  } | null;
  submissions: Pick<Submission, 'uuid' | 'status' | 'owner' | 'public'>[];
};

type useAssignmentsProps = {
  courseUuid: string | null;
  teachingUnitUuid?: string;
  assignmentUuid?: string;
};

export const useAssignments = ({ courseUuid, teachingUnitUuid, assignmentUuid }: useAssignmentsProps) => {
  const { t } = useTranslation();
  const { client: matrixClient } = useMatrixClient();

  const { ConfirmationDialog, confirm: confirmDelete } = useConfirm({
    defaultTitle: t('delete.headline'),
    defaultMessage: t('delete.message', {
      type: t('assignments.title', { count: 1 }),
      context: 'female',
    }),
    defaultConfirmText: t('delete.delete'),
  });

  const [, deleteAssignment] = useMutation(DeleteAssignmentMutation);

  const [uuid, setUuid] = useState<string | null>(assignmentUuid ?? null);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<string | 'table' | null>(null);

  const [{ data }] = useAutoUpdateQuery(
    useQuery({
      query: UseAssignmentsQuery,
      variables: { courseUuid: courseUuid ?? undefined, teachingUnitUuid, assignmentUuid },
    }),
    30,
  );

  const assignments: Assignment[] =
    data?.assignments
      .map((a) => ({
        uuid: a.uuid,
        title: a.title,
        visibleFrom: dayjs(a.visibleFrom?.date).toDate(),
        dueDate: dayjs(a.dueDate?.date).toDate(),
        createdAt: dayjs(a.created).toDate(),
        allowSubmissionAfterDueDate: a.allowSubmissionAfterDueDate ?? false,
        course: {
          uuid: a.course.uuid,
          title: a.course.name,
          organizationUuid: a.course.organization.uuid,
          subject: a.course.subject
            ? {
                uuid: a.course.subject.uuid,
                name: a.course.subject.name,
                shortName: a.course.subject.shortName,
              }
            : null,
        },
        teachingUnit: {
          uuid: a.holder.uuid,
          title: a.holder.title,
        },
        material: a.material
          ? {
              uuid: a.material.uuid,
              text: a.material.text ?? null,
              files: a.material.fileEntries.map((f) => ({
                ...f,
                publicDownloadToken: f.publicDownloadToken ?? null,
              })),
            }
          : null,
        submissions: a.submissions.map((s) => ({
          uuid: s.uuid,
          status: s.status,
          public: s.public ?? false,
          owner: {
            uuid: s.owner.uuid,
            name: s.owner.displayName ?? '',
          },
        })),
      }))
      .sort((a, b) => {
        const aDate = dayjs(a.visibleFrom);
        const bDate = dayjs(b.visibleFrom);
        if (aDate.isBefore(bDate, 'days')) return -1;
        if (aDate.isAfter(bDate, 'days')) return 1;
        return naturalStringSort(a.title, b.title);
      }) ?? [];

  const activeAssignments: Assignment[] = assignments
    .sort((a, b) => {
      const aDate = dayjs(a.dueDate);
      const bDate = dayjs(b.dueDate);
      if (aDate.isBefore(bDate, 'days')) return -1;
      if (aDate.isAfter(bDate, 'days')) return 1;
      return naturalStringSort(a.title, b.title);
    })
    .filter((a) => {
      const today = dayjs();
      return dayjs(a.visibleFrom).isSameOrBefore(today, 'days') && dayjs(a.dueDate).isSameOrAfter(today, 'days');
    });

  const futureAssignments: Assignment[] = assignments.filter((a) => {
    const today = dayjs();
    return dayjs(a.visibleFrom).isAfter(today, 'days');
  });

  const pastAssignments: Assignment[] = assignments.filter((a) => {
    const today = dayjs();
    return dayjs(a.dueDate).isBefore(today, 'days');
  });

  function getAssignmentPath(assignmentUuid: string): string {
    return `/courses/${courseUuid}/assignments/${assignmentUuid}`;
  }

  function handleCreate() {
    setUuid(null);
    setModalOpen(true);
  }

  function handleEdit(assignmentUuid: string) {
    setUuid(assignmentUuid);
    setModalOpen(true);
  }

  function handleCardDelete(assignmentUuid: string) {
    handleDelete(assignmentUuid, 'card');
  }

  function handleTableDelete(assignmentUuid: string) {
    handleDelete(assignmentUuid, 'table');
  }

  async function handleDelete(assignmentUuid: string, type: 'card' | 'table' = 'table') {
    const confirm = await confirmDelete();
    if (!confirm) return;
    setLoading(type === 'card' ? assignmentUuid : 'table');

    const { error } = await deleteAssignment({
      where: { uuid: assignmentUuid },
    });

    courseUuid &&
      matrixClient?.sendNotice(
        courseUuid,
        JSON.stringify({
          body: {
            type: 'assignmentUpdated',
            subjectName: assignments.find((a) => a.uuid === assignmentUuid)?.title,
            groupUuid: courseUuid,
          },
          msgtype: 'm.notice',
        }),
      );

    if (error) {
      showUserDbErrorToast();
    } else {
      showSuccessDeleteToast();
    }
    setLoading(null);
  }

  function handleClose() {
    setUuid(null);
    setModalOpen(false);
  }

  return {
    assignments,
    activeAssignments,
    futureAssignments,
    pastAssignments,
    modalState: {
      assignmentUuid: uuid,
      isOpen: modalOpen,
      onClose: handleClose,
    },
    ConfirmationDialog,
    loading,
    getAssignmentPath,
    handleCreate,
    handleEdit,
    handleDelete,
    handleCardDelete,
    handleTableDelete,
  };
};
